import React, { useState, useMemo, useEffect } from "react";
import { Check, Globe } from "lucide-react";
import { pb } from "@/lib/pocketbase";
import { useSearchParams } from "react-router-dom";

// Update Translation interface to match API response
interface Translation {
  collectionId: string;
  collectionName: string;
  collection_name: string;
  id: string;
  record_id: string;
  translations: {
    np: {
      name: string;
      shortDescription: string;
      description: string;
      source: string;
      about: string;
      viewMore: string;
    }
  };
}

// Update HeroSectionProps
interface HeroSectionProps {
  product: {
    id: string;
    collectionId: string;
    collectionName: string;
    name: string;
    image: string;
    source?: string;
    shortDescription: string;
    translations?: Translation; // Update this to use the full Translation type
  };
}

export const HeroSection: React.FC<HeroSectionProps> = ({ product }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [locale, setLocale] = useState(searchParams.get('locale') || 'en');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const urlLocale = searchParams.get('locale');
    if (urlLocale && (urlLocale === 'en' || urlLocale === 'np')) {
      setLocale(urlLocale);
    }
  }, [searchParams]);

  const toggleLanguage = () => {
    const newLocale = locale === 'en' ? 'np' : 'en';
    setLocale(newLocale);
    setSearchParams({ locale: newLocale });
  };

  const getTranslatedContent = (field: 'name' | 'shortDescription' | 'source') => {
    if (locale === 'en') {
      return product[field];
    }
    
    // Check if translations exist and have Nepali content
    if (product?.translations?.translations?.np) {
      const npContent = product.translations.translations.np[field];
      if (npContent) {
        return npContent;
      }
    }
    
    // Fallback to English
    return product[field];
  };

  const imageUrl = useMemo(() => {
    try {
      return pb.files.getURL(product, product.image);
    } catch (error) {
      return null;
    }
  }, [product]);

  // Add a utility function for dynamic class names
  const getTextClasses = (isHeading: boolean) => {
    const baseClasses = "text-[rgba(0,63,40,1)]";
    const fontClasses = locale === 'np' ? "font-noto" : "font-slate";
    const sizeClasses = isHeading ? "text-4xl font-bold mb-1" : "text-lg font-normal";
    
    return `${baseClasses} ${fontClasses} ${sizeClasses}`;
  };

  return (
    <div className="flex flex-col items-center w-full relative px-4 pt-8 pb-6 bg-[#FFED5A] rounded-b-[90px]">
      {/* Updated header with logo and language toggle */}
      <div className="flex justify-between items-center w-full mb-24 px-4">
        <a 
          href="https://dailokrishi.com" 
          rel="noopener noreferrer"
          className="flex items-center"
        >
          <img
            src="/logo/dailokrishi.png"
            alt="DailoKrishi Logo"
            className="w-16 h-auto cursor-pointer hover:opacity-80 transition-opacity"
          />
        </a>
        
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,63,40,0.1)] hover:bg-[rgba(0,63,40,0.2)] transition-colors"
        >
          <Globe className="w-4 h-4 text-[rgba(0,63,40,1)]" />
          <span className="text-[rgba(0,63,40,1)] font-medium">
            {locale === 'en' ? 'नेपाली' : 'English'}
          </span>
        </button>
      </div>

      {/* Updated Product image section */}
      <div className="w-full h-full relative mb-16 mt-8">
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {!imageError ? (
            <img
              src={imageUrl || '/placeholder.jpg'}
              alt={product.name}
              className={`w-[250px] h-[250px] object-contain`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-[250px] h-[250px] bg-white/50 rounded-lg flex items-center justify-center">
              <span className="text-[rgba(0,63,40,1)]">Image not available</span>
            </div>
          )}
        </div>
      </div>
      <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2">
        <div className="bg-[rgba(0,63,40,1)] rounded-full p-2.5 flex items-center justify-center shadow-lg">
          <Check className="w-10 h-10 text-white " />
        </div>
      </div>
      <div className="text-center mt-12">
        <h1 className={getTextClasses(true) + " whitespace-pre-line"}>
          {getTranslatedContent('name')}
        </h1>
        <h2 className={getTextClasses(true) + "text-[rgba(0,63,40,1)] text-xl mb-2"}>
          Source: {getTranslatedContent('source') || 'Local'}
        </h2>
        <p className={`${getTextClasses(false)} max-w-[260px] mx-auto leading-snug mb-10`}>
          {getTranslatedContent('shortDescription')}
        </p>
      </div>
    </div>
  );
};